import {organizationApiClient} from 'organization/organizationApiClient'
import React, {useEffect, useState} from 'react'
import {useRouteOrganizationEventId} from 'utils/lib/url'
import {toast} from 'react-toastify'
import {EventModel} from 'event/models/Event'

export interface ProductLink {
  name: string
  url: string
}
export const DigitalProductType = 'digital' as const
export const PhysicalProductType = 'physical' as const

export type ProductType = typeof DigitalProductType | typeof PhysicalProductType

export interface Product {
  id?: number
  name: string
  description: string
  price: number | string
  sales?: number
  status: boolean
  links: ProductLink[]
  type: ProductType
  need_phone: boolean
  need_name: boolean
  need_address: boolean
  quantity: number
}

export type AttendeeProduct = Omit<Product, 'sales'> & {
  purchase_date: Date
  purchase_id: number
  event: EventModel
}

export interface EventProductsContextProps {
  products: Product[]
  loading: boolean
  page: number
  totalPages: number
  changePage: (e: any, page: number) => void
  addProduct: (product: Product) => Promise<boolean>
  updateProduct: (product: Product) => Promise<boolean>
  deleteProduct: (product: Product) => Promise<boolean>
}

export const EventProductsContext = React.createContext<
  EventProductsContextProps | undefined
>(undefined)

export default function EventProductsProvider(props: {
  children: React.ReactElement
}) {
  const apiClient = organizationApiClient()
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [page, setPage] = useState<number>(1)
  const eventId = useRouteOrganizationEventId()

  useEffect(() => {
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async (getPage?: number) => {
    setLoading(true)
    const selectedPage = getPage ?? page

    await apiClient
      .get(`api/event/${eventId}/products?page=${selectedPage}`)
      .then((response) => {
        setProducts(response.data)
        setTotalPages(response.last_page)
        setPage(response.current_page)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const changePage = (e: any, toPage: number) => {
    setPage(toPage)
    get(toPage)
  }

  const addProduct = async (product: Product) => {
    setLoading(true)

    const result = await apiClient
      .post(`api/event/${eventId}/products`, product)
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => {
        setLoading(false)
      })

    return result
  }

  const updateProduct = async (product: Product) => {
    setLoading(true)

    const result = await apiClient
      .post(`api/event/${eventId}/products/${product.id}`, product)
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => {
        setLoading(false)
      })

    return result
  }

  const deleteProduct = async (product: Product) => {
    setLoading(true)

    const result = await apiClient
      .remove(`api/event/${eventId}/products/${product.id}`)
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => {
        setLoading(false)
      })

    return result
  }

  return (
    <EventProductsContext.Provider
      value={{
        products,
        loading,
        page,
        changePage,
        totalPages,
        addProduct,
        updateProduct,
        deleteProduct,
      }}
    >
      {props.children}
    </EventProductsContext.Provider>
  )
}

export function useEventProducts() {
  const context = React.useContext(EventProductsContext)
  if (context === undefined) {
    throw new Error(
      'useEventProducts must be used within EventProductsProvider',
    )
  }
  return context
}
